import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SEO from "@/components/seo/SEO";
import { GoldButton, Eyebrow, AmbientOrbs } from "@/components/common/ui";
import { Quotes } from "@phosphor-icons/react";
import { MANIFESTO } from "@/data/company";

export default function Manifesto() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const isNl = lang === "nl";
  const principles = MANIFESTO.map((p) => p[lang]);

  return (
    <>
      <SEO
        title={isNl ? "Ons Hospitality Manifesto" : "Our Hospitality Manifesto"}
        path="/manifesto"
        description={isNl ? "Onze filosofie over technologie in hospitality." : "Our philosophy on technology in hospitality."}
      />

      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs variant="gold" />
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-10">
          <Eyebrow>{isNl ? "Ons manifesto" : "Our manifesto"}</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 font-serif text-5xl leading-[1.02] tracking-tight sm:text-6xl md:text-7xl"
          >
            {isNl ? "Hotels zijn gebouwd rond mensen." : "Hotels are built around people."}
          </motion.h1>
          <p className="mt-6 font-serif text-3xl italic leading-tight text-bht-gold sm:text-4xl">
            {isNl ? "Niet rond software." : "Not around software."}
          </p>
        </div>
      </section>

      <section className="relative border-y border-white/[0.06] py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="space-y-8">
            {principles.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-start gap-6 border-b border-white/[0.06] pb-8 last:border-b-0"
                data-testid={`manifesto-${i}`}
              >
                <div className="font-mono text-[11px] leading-none tracking-widest text-bht-gold shrink-0 pt-2">{String(i + 1).padStart(2, "0")}</div>
                <p className="font-serif text-2xl leading-snug text-white sm:text-3xl">{p}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 rounded-2xl border border-bht-gold/30 bg-bht-gold/5 p-10 text-center">
            <Quotes size={26} weight="fill" className="mx-auto mb-5 text-bht-gold" />
            <p className="font-serif text-2xl leading-snug text-white sm:text-3xl">
              {isNl
                ? "De beste hoteltechnologie is die waarvan uw team vergeet dat ze het gebruikt."
                : "The best hotel technology is the one your team forgets it is using."}
            </p>
            <div className="mt-6 font-mono text-[11px] uppercase tracking-widest text-bht-gold/70">
              — {isNl ? "Ons uitgangspunt, elke dag" : "Our compass, every day"}
            </div>
          </div>

          <div className="mt-16 text-center">
            <GoldButton to={`/${lang}/book-demo`}>{isNl ? "Zie BHT One" : "See BHT One"}</GoldButton>
          </div>
        </div>
      </section>
    </>
  );
}
